import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { ApiError, auth, type CandidateMe } from '../lib/apiClient';

/**
 * Attach (or clear) the candidate's identity on the Sentry scope so subsequent error
 * events carry user info. Strict PII filter — only the stable id + the pool segment.
 * Email, name, region, and verification flag are intentionally never sent.
 *
 * Why both `Sentry.setUser` AND `Sentry.getCurrentScope().setUser`?
 *
 * Sentry v10 separates the "isolation scope" from the "current scope". `Sentry.setUser`
 * writes to the isolation scope only, and the React `<Sentry.ErrorBoundary>` captures
 * via `Sentry.withScope(...)` which forks the current scope — which in browser SDK does
 * not always inherit the isolation scope's user reliably. Setting on both scopes makes
 * the user data appear regardless of which scope a subsequent capture happens against.
 *
 * (Initial Sentry-wiring commit `7b5f5cf` only used `Sentry.setUser`; in practice the
 * captured events arrived in Sentry with `user: { geo: {...} }` only — no id/segment.
 * This follow-up wires both scopes + drives it from a `useEffect` so the call is
 * guaranteed to run after every candidate state transition, including the bootstrap
 * one that fires on page load when a candidate cookie is already present.)
 */
function syncSentryUser(candidate: CandidateMe | null): void {
  if (candidate) {
    const user = { id: candidate.id, segment: 'candidate' };
    Sentry.setUser(user);
    Sentry.getCurrentScope().setUser(user);
  } else {
    Sentry.setUser(null);
    Sentry.getCurrentScope().setUser(null);
  }
}

interface AuthContextValue {
  candidate: CandidateMe | null;
  /** True only while the initial `/candidates/me` bootstrap is in-flight. */
  isLoading: boolean;
  /** Updates context after a successful signup/signin/reset (which set cookies + return CandidateMe). */
  setCandidate: (c: CandidateMe | null) => void;
  /** Calls /signout, clears local state, redirects to home. Safe to invoke even if already signed out. */
  signOut: () => Promise<void>;
  /** Forces a re-fetch of `/candidates/me` — used after email verification etc. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [candidate, setCandidate] = useState<CandidateMe | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  /**
   * Mirror the candidate state onto the Sentry scope. A `useEffect` keyed on `candidate`
   * runs after every commit, so this fires for the bootstrap-on-mount transition
   * (null → me when a cookie is already present), every signin/signup/reset that calls
   * `setCandidate(me)`, and every signout/expired-session path that calls `setCandidate(null)`.
   */
  useEffect(() => {
    syncSentryUser(candidate);
  }, [candidate]);

  const bootstrap = useCallback(async () => {
    try {
      const me = await auth.me();
      setCandidate(me);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setCandidate(null);
      } else {
        // Network or unexpected error — leave as signed out, log nothing (no console.* in prod).
        setCandidate(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const signOut = useCallback(async () => {
    try {
      await auth.signout();
    } catch {
      // Best-effort: even if the server call fails (expired token, etc.), clear local state.
    }
    setCandidate(null);
    navigate('/');
  }, [navigate]);

  const value = useMemo<AuthContextValue>(
    () => ({
      candidate,
      isLoading,
      setCandidate,
      signOut,
      refresh: bootstrap,
    }),
    [candidate, isLoading, signOut, bootstrap],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

export function useCandidate(): CandidateMe | null {
  return useAuth().candidate;
}
